import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentClient } from '@/api/grpc';
import { useAuthedRpc } from '@/hooks/common';
import { makeTripPath, makePaymentPath } from '@/lib/proto';
import { create } from '@bufbuild/protobuf';
import { 
  Payment,
  PaymentPath,
  TripPath,
  PaymentStatus,
  PaymentSchema
} from '@generated/proto/model';
import {
  CreatePaymentRequestSchema,
  ConfirmPaymentRequestSchema,
  GetPaymentRequestSchema,
  ListPaymentsRequestSchema,
  CancelPaymentRequestSchema
} from '@generated/proto/service';

// Create payment
export function useCreatePayment() {
  const { run, loading, error } = useAuthedRpc(
    async (
      user,
      tripId: string,
      payment: Partial<Payment>
    ) => {
      if (!tripId?.trim()) throw new Error("tripId is required.");

      const request = create(CreatePaymentRequestSchema, {
        tripPath: makeTripPath(user.uid, tripId),
        payment: create(PaymentSchema, payment as any),
      });

      return await paymentClient.createPayment(request as any);
    }
  );

  return { createPayment: run, loading, error };
}

// Get specific payment
export function useGetPayment(paymentPath?: PaymentPath) {
  return useQuery({
    queryKey: ['payment', paymentPath?.userId, paymentPath?.tripId, paymentPath?.paymentId],
    queryFn: async () => {
      if (!paymentPath) return null;
      
      const request = { paymentPath };
      const response = await paymentClient.getPayment(request);
      return response.payment;
    },
    enabled: !!paymentPath,
  });
}

// List payments for a trip
export function useListPayments(tripPath?: TripPath, filters?: {
  bookingId?: string;
  status?: PaymentStatus;
}) {
  return useQuery({
    queryKey: ['payments', tripPath?.userId, tripPath?.tripId, filters],
    queryFn: async () => {
      if (!tripPath) return [];
      
      const request = {
        tripPath,
        bookingId: filters?.bookingId,
        status: filters?.status,
      };
      
      const response = await paymentClient.listPayments(request);
      return response.payments || [];
    },
    enabled: !!tripPath,
  });
}

// Confirm payment
export function useConfirmPayment() {
  const { run, loading, error } = useAuthedRpc(
    async (
      user,
      tripId: string,
      paymentId: string,
      paymentMethodId?: string
    ) => {
      if (!tripId?.trim()) throw new Error("tripId is required.");
      if (!paymentId?.trim()) throw new Error("paymentId is required.");

      const request = create(ConfirmPaymentRequestSchema, {
        paymentPath: makePaymentPath(user.uid, tripId, paymentId),
        paymentMethodId,
      });

      return await paymentClient.confirmPayment(request as any);
    }
  );

  return { confirmPayment: run, loading, error };
}

// Cancel payment
export function useCancelPayment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ paymentPath, reason }: { 
      paymentPath: PaymentPath; 
      reason: string;
    }) => {
      const request = {
        paymentPath,
        cancellationReason: reason,
      };
      
      const response = await paymentClient.cancelPayment(request);
      return response.payment;
    },
    onSuccess: (_, variables) => {
      // Invalidate payment and payments list
      queryClient.invalidateQueries({ 
        queryKey: ['payment', variables.paymentPath.userId, variables.paymentPath.tripId, variables.paymentPath.paymentId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['payments', variables.paymentPath.tripId] 
      });
    },
  });
}